import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const Section = ({ children, className = '' }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: '-80px' }}
    variants={fadeUp}
    className={`py-16 sm:py-24 ${className}`}
  >
    {children}
  </motion.section>
);

const SectionLabel = ({ children }) => (
  <p className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.18em] text-[hsl(var(--silver))] mb-6">
    {children}
  </p>
);

export default function IdeaClarityPage() {
  usePageTitle('The Idea Clarity Session');
  const [purchasing, setPurchasing] = useState(false);
  const [checkoutEmail, setCheckoutEmail] = useState('');
  const [checkoutName, setCheckoutName] = useState('');

  const handlePurchase = async () => {
    if (!checkoutEmail) {
      toast.error('Please enter your email address.');
      return;
    }
    setPurchasing(true);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/checkout', {
        product_slug: 'idea-clarity-session',
        origin_url: originUrl,
        quantity: 1,
        customer_email: checkoutEmail,
        customer_name: checkoutName
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment processing error. Please try again.');
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* ── HERO ──────────────────────────────────────────── */}
      <section className="hero-gradient relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1658237775407-aed6450f5851?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
          >
            <motion.p variants={fadeUp} className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.22em] text-[hsl(var(--silver))] mb-8">
              The Cosmic Synthesist &middot; A 90-Minute Working Session
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.1] text-[hsl(var(--foreground))] mb-8">
              You don't have a clarity problem.<br />
              You have a <em className="font-normal italic text-[hsl(var(--champagne))]">framework</em> problem.
            </motion.h1>
            <motion.p variants={fadeUp} className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              You have a big idea living in you. You've tried to map it onto every template, system, and strategy you could find. None of them fit — not because the idea isn't real, but because the tools were never built for the way your mind works.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── IS THIS YOU ───────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionLabel>Is this you</SectionLabel>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold leading-[1.15] text-[hsl(var(--foreground))] mb-6">
            You know something is there.<br />
            You just can't <em className="font-normal italic text-[hsl(var(--champagne))]">see</em> it yet.
          </h2>
          <p className="font-['Inter'] text-[15px] sm:text-base leading-[1.8] text-[hsl(var(--muted-foreground))] mb-10">
            This session is for the woman who is intelligent, visionary, and completely overwhelmed — not because she lacks capability, but because she's been trying to pour a large, unconventional idea into a container that was never designed for it.
          </p>
          <ul className="space-y-5 mb-12">
            {[
              "You have an idea that feels enormous but you can't explain it clearly — even to yourself",
              "You've downloaded the templates, watched the tutorials, maybe hired a coach — and nothing clicked",
              "You think in connections, patterns, and possibilities, which makes knowing where to start feel impossible",
              "Every time you try to plan it out, you end up more confused than when you started",
              "You're not looking for someone to hand you a roadmap — you need someone to help you see what you're actually building",
            ].map((item, i) => (
              <li key={item.slice(0,30)} className="flex items-start gap-4">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold))] shrink-0" />
                <span className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))]">{item}</span>
              </li>
            ))}
          </ul>
          <blockquote className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic leading-[1.4] text-[hsl(var(--foreground))] border-l-2 border-[hsl(var(--gold))] pl-6 py-2">
            The reason other people's frameworks don't work for you isn't a flaw.<br />
            It's the signal that you're thinking at a scale they weren't designed for.
          </blockquote>
        </div>
      </Section>

      {/* ── WHAT HAPPENS ──────────────────────────────────── */}
      <Section className="section-mist">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionLabel>What happens in the session</SectionLabel>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold leading-[1.15] text-[hsl(var(--foreground))] mb-6">
            One focused conversation.<br />
            <em className="font-normal italic text-[hsl(var(--champagne))]">Uncommon</em> clarity.
          </h2>
          <p className="font-['Inter'] text-[15px] sm:text-base leading-[1.8] text-[hsl(var(--muted-foreground))] mb-12">
            This isn't a coaching intake call. It's a working session — we go in, we find the shape of your idea, and you leave with something real. In 90 minutes together, you'll walk away with:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { num: '01', title: 'A clear articulation of what your idea actually is', desc: 'Often different — and sharper — than what you thought it was going in.' },
              { num: '02', title: 'The core problem it solves and who it truly serves', desc: 'Not a target market exercise. A real reckoning with what your idea is for.' },
              { num: '03', title: 'Your one native first move', desc: 'Not a generic next step. The move that\'s true to how you think and how your idea wants to grow.' },
              { num: '04', title: 'A framework built with you — not handed to you', desc: 'Because a framework only works when it was made for the way your mind moves.' },
            ].map((d) => (
              <div
                key={d.num}
                className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 transition-all duration-300 hover:border-[rgba(201,168,76,0.35)] hover:bg-[rgba(27,77,68,0.12)]"
              >
                <p className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.18em] text-[hsl(var(--gold))] mb-3">{d.num}</p>
                <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-semibold italic text-[hsl(var(--foreground))] mb-3 leading-[1.25]">{d.title}</p>
                <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── WHAT IT IS NOT ────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionLabel>What this is not</SectionLabel>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold leading-[1.15] text-[hsl(var(--foreground))] mb-6">
            No curriculum.<br />
            No <em className="font-normal italic text-[hsl(var(--champagne))]">generic</em> advice.
          </h2>
          <p className="font-['Inter'] text-[15px] sm:text-base leading-[1.8] text-[hsl(var(--muted-foreground))] mb-8">
            Let's be honest about what you don't need more of:
          </p>
          <ul className="space-y-4 mb-10">
            {[
              'Another business plan template',
              'A 12-week program before you see any results',
              'Advice that assumes your idea fits a known category',
              "Someone who will tell you to niche down before they've even understood what you're building",
              'Homework, modules, or a portal to log into',
            ].map((item, i) => (
              <li key={item.slice(0,30)} className="flex items-start gap-4">
                <span className="mt-1.5 font-['Inter'] text-xs text-[hsl(var(--rose))]">&mdash;</span>
                <span className="font-['Inter'] text-[15px] leading-[1.7] text-[hsl(var(--muted-foreground))]">{item}</span>
              </li>
            ))}
          </ul>
          <p className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--foreground))]">
            Just one session. One honest conversation. One significant shift in how clearly you can see the thing you've been carrying.
          </p>
        </div>
      </Section>

      {/* ── THE OFFER ─────────────────────────────────────── */}
      <Section className="section-mist">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <SectionLabel>The offer</SectionLabel>
          <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.25)] p-8 sm:p-12 shadow-[0_18px_60px_rgba(0,0,0,0.45)] relative overflow-hidden">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-12 h-12 border-t border-l border-[rgba(201,168,76,0.3)]" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-b border-r border-[rgba(201,168,76,0.3)]" />

            <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-3">
              The Idea Clarity Session
            </p>
            <p className="font-['Inter'] text-sm text-[hsl(var(--silver))] mb-6">
              90 minutes &middot; 1:1 &middot; Virtual
            </p>
            <p className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--gold))] mb-2">
              $247
            </p>
            <p className="font-['Inter'] text-xs text-[hsl(var(--muted-foreground))] mb-8">
              One-time &middot; No package required
            </p>
            <div className="space-y-3 max-w-xs mx-auto mb-4">
              <Input
                type="text"
                value={checkoutName}
                onChange={e => setCheckoutName(e.target.value)}
                placeholder="Your name"
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
              />
              <Input
                type="email"
                value={checkoutEmail}
                onChange={e => setCheckoutEmail(e.target.value)}
                placeholder="Your email address"
                className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] font-['Inter'] text-sm"
                data-testid="clarity-session-email-input"
              />
              <Button
                onClick={handlePurchase}
                disabled={purchasing}
                className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium py-5 rounded-lg hover:brightness-[1.03] disabled:opacity-50"
                data-testid="clarity-session-book-button"
              >
                {purchasing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  'Book Your Session'
                )}
              </Button>
            </div>
            <p className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
              Limited spots available each month
            </p>
          </div>
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mt-8">
            Not sure if this is right for you?{' '}
            <a
              href="mailto:emma@thecosmicsynthesist.com"
              className="text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors"
            >
              Send a note first.
            </a>
            <br />
            If it's not the right fit, I'll tell you honestly.
          </p>
        </div>
      </Section>

      {/* ── ABOUT ─────────────────────────────────────────── */}
      <Section>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionLabel>Who you're working with</SectionLabel>
          <p className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))] mb-1">Emma</p>
          <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--rose))] mb-8">The Cosmic Synthesist</p>
          <div className="font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))] space-y-6">
            <p>
              I'm a cross-dimensional thinker who has spent years at the intersection of science, systems, and intuition — building things that didn't have a blueprint because no blueprint existed yet.
            </p>
            <p>
              I'm not a generalist who dabbles. I'm a synthesist — someone who sees the architecture underneath an idea before the walls go up. I've built R&D departments, fermentation systems, content ecosystems, and technology platforms. What I've learned across all of it is that the biggest ideas don't fail because of execution. They fail because no one helped the founder see what they were actually building before they started building the wrong version of it.
            </p>
            <p>
              <strong className="text-[hsl(var(--foreground))]">That's what I do in this session.</strong> I help you see it clearly — on its own terms, not someone else's.
            </p>
          </div>
        </div>
      </Section>

      {/* ── GEO DIVIDER ───────────────────────────────────── */}
      <div className="flex justify-center py-8">
        <div className="w-3 h-3 rotate-45 border border-[rgba(201,168,76,0.3)]" />
      </div>

      {/* ── FINAL CTA ─────────────────────────────────────── */}
      <Section>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold leading-[1.15] text-[hsl(var(--foreground))] mb-6">
            Your dream has been waiting<br />long enough.
          </h2>
          <p className="font-['Inter'] text-base leading-[1.7] text-[hsl(var(--muted-foreground))] mb-10">
            One conversation. Ninety minutes. The clarity you've been circling for months.
          </p>
          <Button
            onClick={() => window.scrollTo({ top: document.querySelector('[data-testid="clarity-session-email-input"]')?.closest('section')?.offsetTop - 100, behavior: 'smooth' })}
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-10 py-5 rounded-lg hover:brightness-[1.03] active:scale-[0.98]"
          >
            Book Your Session — $247
          </Button>
          <p className="font-['Inter'] text-xs text-[hsl(var(--silver))] mt-4">
            Spots are limited &middot; Virtual &middot; 90 Minutes
          </p>
        </div>
      </Section>

      <SiteFooter />
    </div>
  );
}
